import React, { useState, useEffect } from 'react';
import { EventViewData } from '../../../../../services/eventService';
import styles from './GroupMatches.module.css';
interface GroupMatchesProps {
  eventData: EventViewData;
  onGenerateMatches: () => void;
  handleShowUnmatchedUsers: (roundNumber?: number) => void;
  handleDeleteGroup: (roundNumber: number, groupId: string) => Promise<void>;
  handleRemoveUserFromGroup: (roundNumber: number, groupId: string, userId: string) => Promise<void>;
  handleAddUserToGroup: (groupId: string, userId: string) => Promise<void>;
  fetchUnmatchedUsers: () => Promise<any>;
  handleGenerateMatches: () => void;
  handleDownloadExcel: () => void;
  handleShowAddUsersModal: (roundNumber: number, groupId: string) => void;

}

const GroupMatches: React.FC<GroupMatchesProps> = ({
  eventData,
  onGenerateMatches,
  handleShowUnmatchedUsers,
  handleDeleteGroup,
  handleRemoveUserFromGroup,
  handleAddUserToGroup,
  fetchUnmatchedUsers,
  handleGenerateMatches,
  handleDownloadExcel,
  handleShowAddUsersModal,
}) => {
  const [animationClass, setAnimationClass] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setAnimationClass(styles.fadeInUp);
  }, []);

  const handleGenerateWithAnimation = async () => {
    setIsLoading(true);
    // Call both handlers to ensure compatibility
    await handleGenerateMatches();
    onGenerateMatches();
    setIsLoading(false);
  };

  return (
    <div className={`card p-4 role-container shadow-sm ${animationClass}`}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerContent}>
          <div className={styles.iconWrapper}>
            <i className="bi bi-people-fill"></i>
          </div>
          <div>
            <h3 className={styles.sectionTitle}>Group Matches</h3>
            <p className={styles.sectionSubtitle}>Manage and view generated match groups</p>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <button 
            className={`btn btn-outline-primary ${styles.actionButton}`}
            onClick={handleGenerateWithAnimation}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="bi bi-arrow-repeat me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-shuffle me-2"></i>
                Regenerate Groups
              </>
            )}
          </button>
          <button 
            className={`btn btn-outline-info ${styles.actionButton}`}
            onClick={() => handleShowUnmatchedUsers()}
          >
            <i className="bi bi-person-plus me-2"></i>
            Show Unmatched Users
          </button>
          <button 
            className={`btn btn-outline-success ${styles.actionButton}`}
            onClick={handleDownloadExcel}
          >
            <i className="bi bi-file-earmark-excel me-2"></i>
            Export Excel
          </button>
        </div>
      </div>

      {eventData.best_matches && eventData.best_matches.length > 0 ? (
        <div className={styles.roundsGrid}>
          {eventData.best_matches.map((roundData, roundIndex) => {
            // Debug log
            console.log(`Round ${roundData.round_number}:`, roundData);
            
            return (
              <div key={roundIndex} className={styles.roundCard}>
                <div className={styles.roundHeader}>
                  <div className={styles.roundInfo}>
                    <div className={styles.roundBadge}>
                      <i className="bi bi-clock-history me-2"></i>
                      Round {roundData.round_number}
                    </div>
                    <div className={styles.roundStats}>
                      <span className={styles.groupCount}>
                        {roundData.groups?.length || 0} Groups
                      </span>
                      <span className={styles.memberCount}>
                        {roundData.groups?.reduce((sum, group) => sum + group.group_size, 0) || 0} Members
                      </span>
                      <button 
                        className={`btn btn-sm btn-outline-light ${styles.roundActionBtn}`}
                        onClick={() => handleShowUnmatchedUsers(roundData.round_number)}
                        title="Show unmatched users for this round"
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        Unmatched
                      </button>
                    </div>
                  </div>
                </div>

                <div className={styles.roundContent}>
                  {roundData.groups && roundData.groups.length > 0 ? (
                    <div className={styles.groupsGrid}>
                      {roundData.groups.map((group, groupIndex) => {
                        // Debug log for group structure
                        console.log(`Group ${groupIndex}:`, group);
                        
                        // Determine if this is an "odd" group (has one more member than normal)
                        const totalMembers = roundData.groups.reduce((sum, g) => sum + g.group_size, 0);
                        const normalGroupSize = Math.floor(totalMembers / roundData.groups.length);
                        const isOddGroup = group.group_size > normalGroupSize;
                        
                        // Get the correct group ID (try different possible field names)
                        const groupId = group.group_id ?? group.Group_ID ?? group.id;
                        
                        // Debug logging to see what's in the group object
                        console.log(`🔍 Group ${groupIndex} debug:`, {
                          group,
                          groupId,
                          keys: Object.keys(group),
                          fallbackToIndex: groupId === undefined
                        });
                        
                        // If no valid ID found, log error and skip this group
                        if (groupId === undefined) {
                          console.error(`❌ No valid group ID found for group at index ${groupIndex}:`, group);
                          return null;
                        }
                        
                        return (
                          <div 
                            key={groupIndex} 
                            className={`${styles.groupCard} ${isOddGroup ? styles.oddGroup : ''}`}
                          >
                            <div className={styles.groupHeader}>
                              <div className={styles.groupTitle}>
                                <i className="bi bi-people me-2"></i>
                                Group {groupIndex + 1}
                                {isOddGroup && (
                                  <span className={styles.oddBadge} title="This group has one extra member">
                                    +1
                                  </span>
                                )}
                              </div>
                              <div className={styles.groupActions}>
                                <span className={styles.membersBadge}>
                                  {group.group_size} members
                                </span>
                                <button 
                                  type="button" 
                                  className={styles.addUsersBtn}
                                  onClick={() => handleShowAddUsersModal(roundData.round_number, groupId)}
                                  title="Add users to this group"
                                >
                                  <i className="bi bi-person-plus"></i>
                                </button>
                                <button 
                                  type="button" 
                                  className={styles.deleteGroupBtn}
                                  onClick={() => handleDeleteGroup(roundData.round_number, groupId)}
                                  title="Delete this group"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </div>

                            <div className={styles.groupMembers}>
                              {group.group_members.map((memberId, memberIndex) => {
                                // Debug logging for user matching
                                console.log(`🔍 Looking for user with ID: "${memberId}"`, {
                                  memberId,
                                  memberIdType: typeof memberId,
                                  firstUserId: eventData.users[0]?.User_ID,
                                  firstUserIdType: typeof eventData.users[0]?.User_ID,
                                  totalUsers: eventData.users.length,
                                  totalGroupMembers: group.group_members.length
                                });
                                
                                const user = eventData.users.find(u => u.User_ID === memberId);
                                if (!user) {
                                  console.warn(`❌ User with ID ${memberId} not found in event users`);
                                  // Show first few users for comparison
                                  console.log(`First 3 available users:`, 
                                    eventData.users.slice(0, 3).map(u => ({ id: u.User_ID, name: `${u.firstname} ${u.lastname}` }))
                                  );
                                  return (
                                    <div key={memberIndex} className={styles.memberCard} style={{ backgroundColor: '#ffe6e6', border: '2px solid #ff6b6b' }}>
                                      <div className={styles.memberInfo}>
                                        <div className={styles.memberName} style={{ color: '#d63031' }}>
                                          ⚠️ User not found
                                        </div>
                                        <div className={styles.memberFirm} style={{ color: '#636e72', fontSize: '12px' }}>
                                          ID: {memberId}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return (
                                  <div key={memberIndex} className={styles.memberCard}>
                                    <div className={styles.memberAvatar}>
                                      {user.picture ? (
                                        <img 
                                          src={`data:image/png;base64,${user.picture}`} 
                                          alt={`${user.firstname} ${user.lastname}`}
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className={styles.defaultAvatar}>
                                          <i className="bi bi-person-fill"></i>
                                        </div>
                                      )}
                                    </div>
                                    <div className={styles.memberInfo}>
                                      <div className={styles.memberName}>
                                        {user.firstname} {user.lastname}
                                      </div>
                                      {user.firm && (
                                        <div className={styles.memberFirm}>
                                          <i className="bi bi-building me-1"></i>
                                          {user.firm.abbrev}
                                        </div>
                                      )}
                                      {user.jobtitle && (
                                        <div className={styles.memberTitle}>
                                          <i className="bi bi-briefcase me-1"></i>
                                          {user.jobtitle}
                                        </div>
                                      )}
                                    </div>
                                    <div className={styles.memberActions}>
                                      <button
                                        className={styles.removeUserBtn}
                                        onClick={() => handleRemoveUserFromGroup(roundData.round_number, groupId, user.User_ID)}
                                        title="Remove user from group"
                                      >
                                        <i className="bi bi-x-circle"></i>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {group.created_at && (
                              <div className={styles.groupFooter}>
                                <div className={styles.footerContent}>
                                  <small className={styles.createdTime}>
                                    <i className="bi bi-clock me-1"></i>
                                    Created: {new Date(group.created_at).toLocaleString('de-DE')}
                                  </small>
                                  <small className={styles.groupStats}>
                                    <i className="bi bi-people me-1"></i>
                                    {group.group_size} {group.group_size === 1 ? 'member' : 'members'}
                                  </small>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.emptyRound}>
                      <div className={styles.emptyIcon}>
                        <i className="bi bi-people"></i>
                      </div>
                      <p className={styles.emptyMessage}>No groups generated for this round yet</p>                          
                      <button 
                            className={`btn btn-primary ${styles.generateButton}`}
                            onClick={handleGenerateWithAnimation}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <>
                                <i className="bi bi-arrow-repeat me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                                Generating...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-shuffle me-2"></i>
                                Generate Groups
                              </>
                            )}
                          </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.noRoundsContainer}>
          <div className={styles.emptyStateIcon}>
            <i className="bi bi-calendar-x"></i>
          </div>
          <h4 className={styles.emptyStateTitle}>No Match Rounds Yet</h4>
          <p className={styles.emptyStateMessage}>
            Generate group matches to create organized rounds for your event participants.
          </p>
          <button 
            className={`btn btn-primary ${styles.generateButton}`}
            onClick={handleGenerateWithAnimation}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="bi bi-arrow-repeat me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                Generating First Round...
              </>
            ) : (
              <>
                <i className="bi bi-shuffle me-2"></i>
                Generate First Round
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupMatches;
